// AccountService — the granular fetch + account pick-order logic (contract 07 §8). Pure,
// Vue-free, unit-testable; `ApiError` from the api layer propagates to the caller. Ported 1:1
// from v3 minus `computePermissionsAndPackage`, which moves to `PackageService` (a later task).
// The api now returns unwrapped DTOs, so the `.data`/`.data.data` unwraps are gone here.
import { AccountApi } from '../api/AccountApi'
import type {
  AccountDetail,
  AccountItem,
  UserAccessInfo,
  UserResponse,
} from '../models/account.types'

import { ApiError } from '@/shared/http'

export class AccountService {
  constructor(private readonly api: AccountApi = new AccountApi()) {}

  public async fetchUser(): Promise<UserResponse> {
    return (await this.api.getMe()) ?? {}
  }

  public async fetchAccounts(): Promise<AccountItem[]> {
    const list = await this.api.getAccessibleAccounts()
    return list.filter((account) => account.name).sort((a, b) => Number(b.id) - Number(a.id))
  }

  public pickAccount(params: {
    accounts: AccountItem[]
    userId?: number
    oldAccountId?: number
  }): AccountItem | null {
    const { accounts, userId, oldAccountId } = params
    const first = accounts[0]
    if (!first) return null

    const fallbackAccount =
      accounts.length === 1
        ? first
        : (accounts.find((account) => Number(account.workspace_count) > 0) ?? first)

    if (oldAccountId) {
      const accountMap = new Map<number, AccountItem>()
      for (const account of accounts) accountMap.set(Number(account.id), account)
      return accountMap.get(Number(oldAccountId)) ?? fallbackAccount
    }

    const mainAccount = accounts.find(
      (account) =>
        Number(account.user_id) === Number(userId) && Number(account.workspace_count) > 0,
    )

    return mainAccount ?? fallbackAccount
  }

  public async fetchAccountContext(accountId: number): Promise<{
    detail: AccountDetail | null
    access: UserAccessInfo
  }> {
    const [detail, access] = await Promise.all([
      this.api.getSelectedAccountDetail(accountId),
      this.api.getUserAccessInfo(),
    ])

    return {
      detail: detail ?? null,
      access: access ?? {},
    }
  }

  /**
   * Rename the AUTHENTICATED user (CM-22-05). Ports v3 `AuthService.updateUserName`
   * (`Orphex-New-Front/src/features/auth/services/AuthService.ts:36-39`) — the *instance* method.
   * Its `static` twin (`:41-46`) does NOT come across: that one dynamic-imported `accountStore`
   * to fill the id in, the cross-feature leak contract 06 §3 drops. Here `userId` is a plain
   * argument and the store read happens in `useAccount()`, the layer allowed to do it.
   *
   * Writes nothing to the store: the renamed user reaches the UI through the account-context
   * reload the caller runs next (contract 22 §11 — "each ends with 07's
   * `loadSelectedAccountContext(true)`"), which re-hydrates `session_access.user`.
   *
   * REJECTS with `ApiError` — that is the point of the seam. The v3 call site was `try/finally`
   * with **no `catch`** (`ProfileSettings.vue:85-97`), so a rejected rename fell straight through
   * to a "Username successfully updated." alert. Callers must catch.
   */
  public async renameUser(userId: number | undefined, name: string): Promise<void> {
    // Not defensive noise: `userId` is `undefined` until `session_access` hydrates, and v3 would
    // happily PATCH `/auth/change-username/undefined`. Fail as the one error type above `api/`
    // (ledger A2) instead of shipping a malformed URL.
    if (userId === undefined) {
      throw new ApiError({
        kind: 'validation',
        status: null,
        code: 'USER_ID_REQUIRED',
        fields: {},
        payload: null,
        userVisible: false,
        detail: 'renameUser called with no authenticated user id',
      })
    }
    await this.api.changeUsername(userId, name)
  }
}
